/**
 * Base assessment DTOs
 * Extracted from src/services/patientmr/assessments.service.ts - API contract types only
 */
import { DisciplineApi } from '../../common/discipline.dto';

/**
 * Assessment status enum — canonical API values only.
 * Legacy aliases IN_PROGRESS and QA_APPROVED have been removed; the backend
 * normalizes those values to DRAFT and COMPLETE respectively on read.
 */
export enum AssessmentStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  QA = 'QA',
  COMPLETE = 'COMPLETE',
  CURRENT = 'CURRENT',     // Reserved for Phase 2 — do not use in MVP
  EXPIRED = 'EXPIRED'      // Reserved for Phase 2 — do not use in MVP
}

/**
 * UI-facing Assessment discipline enum
 */
// Phase 20A: registry disciplines
export const AssessmentDiscipline = DisciplineApi;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type AssessmentDiscipline = DisciplineApi;

/**
 * API-facing Assessment discipline enum (matches backend ASSESSMENT_DISCIPLINES: RN/PT/OT/ST/MSW/HHA)
 */
// Phase 20A: registry disciplines
export const ApiAssessmentDiscipline = DisciplineApi;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional type/value pair (enum replacement)
export type ApiAssessmentDiscipline = DisciplineApi;

/**
 * Assessment list item for displaying in lists
 */
export type AssessmentListItem = {
  assessmentId: string;
  date: string;
  status: AssessmentStatus;
  discipline: AssessmentDiscipline;
  providerName: string;
  assessmentType?: string;
};

/**
 * Assessment list response with pagination
 */
export type AssessmentListData = {
  assessments: AssessmentListItem[];
  count: number;
  paginationInfo?: {
    hasMoreItems?: boolean;
    hasMore?: boolean;
    nextToken?: string;
    paginationToken?: string;
  };
  pagination?: {
    hasMoreItems?: boolean;
    hasMore?: boolean;
    nextToken?: string;
    paginationToken?: string;
  };
};

/**
 * Assessment detail entity
 * Contains discipline-specific assessment data
 */
export type AssessmentDetail = {
  assessmentId?: string;
  assessmentType?: string;   // Root-level canonical value (START_OF_CARE, REASSESSMENT, END_OF_CARE, OASIS_*)
  provider: string;
  discipline: AssessmentDiscipline;
  status?: AssessmentStatus;
  data: Record<string, any>;
  /** Record version; starts at 1 and goes up on each reopen (Phase 4) */
  version?: number;
  /** Phase 16C (16B): optimistic-concurrency counter; sent back as expectedVersion on update */
  editVersion?: number;
  /** One row per reopen (Phase 4) */
  reopenHistory?: ReopenHistoryEntry[];
};

/**
 * One reopen of an assessment or care plan (Phase 4). `from` is the status it was reopened from.
 */
export type ReopenHistoryEntry = {
  from?: string;
  by?: string;
  at?: string;
  reason?: string;
};
